import { isCorrectOutcome } from './scoring'
import type { Game, Prediction } from './types'

// How many fixtures must resolve before the hero shows a tier at all — below
// this, the correct-outcome rate is too noisy to color the card by. Mirrors the "at least half,
// capped at 5" rule.
export function guardrailThreshold(totalFixtures: number): number {
  return Math.min(5, Math.ceil(totalFixtures / 2))
}

export interface LiveGameweekTierResult {
  /** Correct-outcome % out of all fixtures in the gameweek (the * "Tier logic" — denominator is the
   * full fixture count, not just the
   * resolved subset, so this rises toward the true rate as the GW plays out
   * rather than jumping straight to it). */
  pct: number
  resolvedFixtures: number
  totalFixtures: number
  guardrailMet: boolean
}

/**
 * Live, per-fixture-progressive correct-outcome rate for the current
 * gameweek — gated per fixture by `isGameRevealed` rather than computed by
 * computeGameweekResult (season.ts), which only ever runs at full-time and isn't reveal-aware.
 */
export function computeLiveGameweekTier(
  games: Game[],
  predictions: Record<string, Prediction>,
  actualResults: Record<string, { homeScore: number; awayScore: number }>,
  isGameRevealed: (gameId: string) => boolean
): LiveGameweekTierResult {
  const totalFixtures = games.length
  const resolvedGames = games.filter(
    (game) => isGameRevealed(game.id) && actualResults[game.id]
  )
  const resolvedFixtures = resolvedGames.length

  const correctOutcomes = resolvedGames.reduce((count, game) => {
    const prediction = predictions[game.id]
    const actual = actualResults[game.id]
    if (!prediction || !actual) return count
    return isCorrectOutcome(
      prediction.homeScore,
      prediction.awayScore,
      actual.homeScore,
      actual.awayScore
    )
      ? count + 1
      : count
  }, 0)

  return {
    pct: totalFixtures === 0 ? 0 : (correctOutcomes / totalFixtures) * 100,
    resolvedFixtures,
    totalFixtures,
    guardrailMet: totalFixtures > 0 && resolvedFixtures >= guardrailThreshold(totalFixtures),
  }
}
